/**
 * useD2Async — render placeholders emitted by `markdownConfig.ts` for
 * ` ```d2 ` code blocks via the official D2.js (WASM) library.
 *
 * D2 (https://d2lang.com) is the state-of-art text-to-diagram language
 * — superior auto-layout (ELK + TALA) to Mermaid, cleaner default
 * styling, sql_table / class shapes built-in. Alex 2026-05-27: "die
 * beste Lösung ist D2".
 *
 * Scans the supplied root for `.d2-async[data-d2-source]` nodes and
 * replaces their content with an SVG. Compile + render are both async
 * (run in a webworker against the WASM binary), so we kick them off
 * with no await-block. Idempotent via `data-d2-rendered`. MutationObserver
 * re-scans on innerHTML changes (CollabReadView polls).
 *
 * Layout engine defaults to ELK (better hierarchical placement than
 * dagre). The library bundles Source Sans Pro by default — we let
 * that ride for now; can swap to a project font later via fontRegular.
 */

import { useEffect } from 'react'
import { D2 } from '@terrastruct/d2'

// Single D2 instance reused across all calls. The class wraps a
// webworker — re-instantiating per render would re-spin the worker.
let _d2: D2 | null = null
function getD2(): D2 {
  if (!_d2) _d2 = new D2()
  return _d2
}

/**
 * Attach to a DOM root once it mounts. Idempotent.
 */
export function useD2Async(root: HTMLElement | null): void {
  useEffect(() => {
    if (!root) return
    const d2 = getD2()
    let cancelled = false
    let counter = 0

    const renderAll = async () => {
      if (cancelled) return
      const placeholders = root.querySelectorAll<HTMLElement>(
        '.d2-async:not([data-d2-rendered])',
      )
      for (const el of Array.from(placeholders)) {
        const source = el.getAttribute('data-d2-source') || ''
        if (!source.trim()) {
          el.setAttribute('data-d2-rendered', 'empty')
          continue
        }
        // Mark in-flight so concurrent observer ticks don't re-render
        // the same node mid-compile.
        el.setAttribute('data-d2-rendered', 'pending')
        try {
          // d2's CompileRequest typings dropped `layout` upstream but
          // the runtime still honours it. Pass the second arg through
          // `as never` — the runtime call is unchanged.
          const result = await d2.compile(
            source,
            { layout: 'elk' } as never,
          )
          if (cancelled) return
          const svg = await d2.render(result.diagram, {
            ...result.renderOptions,
            // Salt prevents duplicate-id collisions if multiple D2
            // diagrams share IDs after compile. Random per render.
            salt: `d2-${Date.now()}-${counter++}`,
            noXMLTag: true,
            pad: 20,
            center: true,
          })
          if (cancelled) return
          el.innerHTML = svg
          el.setAttribute('data-d2-rendered', 'true')
        } catch (e) {
          if (cancelled) return
          const msg = e instanceof Error ? e.message : String(e)
          el.innerHTML =
            `<pre class="d2-error" style="white-space:pre-wrap; ` +
            `padding:12px; background:#fee; border-left:4px solid #c33; ` +
            `font-family:monospace; font-size:13px; color:#600;">` +
            `<strong>D2 render error:</strong>\n${escapeHtml(msg)}\n\n` +
            `<em>Source:</em>\n${escapeHtml(source)}</pre>`
          el.setAttribute('data-d2-rendered', 'error')
          console.warn('D2 render failed', e, source)
        }
      }
    }

    void renderAll()

    let scheduled = false
    const observer = new MutationObserver(() => {
      if (scheduled) return
      scheduled = true
      queueMicrotask(() => {
        scheduled = false
        void renderAll()
      })
    })
    observer.observe(root, { childList: true, subtree: true })

    return () => {
      cancelled = true
      observer.disconnect()
    }
  }, [root])
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}
